/**
 * greekParsing.js
 *
 * Decodes Unbound Bible / Robinson parsing codes into human-readable labels.
 *
 * Examples:
 *   N-NSF   → { pos: 'Noun',        details: ['Nominative', 'Singular', 'Feminine'] }
 *   V-AAI-3S → { pos: 'Verb',       details: ['Aorist', 'Active', 'Indicative', '3rd person', 'Singular'] }
 *   T-ASM   → { pos: 'Article',     details: ['Accusative', 'Singular', 'Masculine'] }
 *   CONJ    → { pos: 'Conjunction', details: [] }
 */

const STANDALONE = {
  CONJ: 'Conjunction',
  COND: 'Conditional particle',
  PRT:  'Particle',
  PREP: 'Preposition',
  ADV:  'Adverb',
  INJ:  'Interjection',
  ARAM: 'Aramaic transliteration',
  HEB:  'Hebrew transliteration',
  INF:  'Infinitive',
};

const POS = {
  N: 'Noun',
  V: 'Verb',
  T: 'Article',
  A: 'Adjective',
  P: 'Personal Pronoun',
  R: 'Relative Pronoun',
  C: 'Reciprocal Pronoun',
  D: 'Demonstrative Pronoun',
  K: 'Correlative Pronoun',
  I: 'Interrogative Pronoun',
  X: 'Indefinite Pronoun',
  Q: 'Correlative/Interrogative Pronoun',
  F: 'Reflexive Pronoun',
  S: 'Possessive Pronoun',
};

const CASE   = { N: 'Nominative', G: 'Genitive', D: 'Dative', A: 'Accusative', V: 'Vocative' };
const NUMBER = { S: 'Singular', P: 'Plural' };
const GENDER = { M: 'Masculine', F: 'Feminine', N: 'Neuter' };
const PERSON = { '1': '1st person', '2': '2nd person', '3': '3rd person' };

const VERB_TENSE = {
  P:  'Present',       I:  'Imperfect',
  F:  'Future',        '2F': 'Second Future',
  A:  'Aorist',        '2A': 'Second Aorist',
  X:  'Perfect',       '2X': 'Second Perfect',
  Y:  'Pluperfect',    '2Y': 'Second Pluperfect',
};
const VERB_VOICE = {
  A: 'Active',   M: 'Middle',   P: 'Passive',
  E: 'Middle/Passive', D: 'Middle Deponent',
  O: 'Passive Deponent', N: 'Middle/Passive Deponent', Q: 'Passive Deponent',
};
const VERB_MOOD = {
  I: 'Indicative', S: 'Subjunctive', O: 'Optative', M: 'Imperative',
  N: 'Infinitive', P: 'Participle',
};

function decodeNominal(posCode, inflection) {
  const pos = POS[posCode] || posCode;
  if (!inflection) return { pos, details: [] };

  // Indeclinable subtypes
  if (inflection === 'PRI') return { pos: 'Proper Noun', details: ['Indeclinable'] };
  if (inflection === 'NUI') return { pos: 'Numeral',     details: ['Indeclinable'] };
  if (inflection === 'OI')  return { pos,                details: ['Indeclinable'] };
  if (inflection === 'LI')  return { pos,                details: ['Letter / Indeclinable'] };

  const details = [];
  if (CASE[inflection[0]])   details.push(CASE[inflection[0]]);
  if (NUMBER[inflection[1]]) details.push(NUMBER[inflection[1]]);
  if (GENDER[inflection[2]]) details.push(GENDER[inflection[2]]);
  return { pos, details };
}

function decodeVerb(parts) {
  // V - <tense><voice><mood> - [<person><number> | <case><number><gender>]
  const tvm   = parts[1] || '';
  const pnCNG = parts[2] || '';
  const details = [];

  let i = 0;
  let tenseKey = '';
  if (tvm[i] === '2') { tenseKey = '2' + tvm[i + 1]; i += 2; }
  else                { tenseKey = tvm[i];             i += 1; }

  const voiceChar = tvm[i];
  const moodChar  = tvm[i + 1];

  if (VERB_TENSE[tenseKey]) details.push(VERB_TENSE[tenseKey]);
  if (VERB_VOICE[voiceChar]) details.push(VERB_VOICE[voiceChar]);
  if (VERB_MOOD[moodChar])  details.push(VERB_MOOD[moodChar]);

  if (moodChar === 'P' && pnCNG.length === 3) {
    // Participle — case/number/gender
    if (CASE[pnCNG[0]])   details.push(CASE[pnCNG[0]]);
    if (NUMBER[pnCNG[1]]) details.push(NUMBER[pnCNG[1]]);
    if (GENDER[pnCNG[2]]) details.push(GENDER[pnCNG[2]]);
  } else if (pnCNG.length >= 2) {
    // Finite verb — person/number
    if (PERSON[pnCNG[0]])  details.push(PERSON[pnCNG[0]]);
    if (NUMBER[pnCNG[1]])  details.push(NUMBER[pnCNG[1]]);
  }

  return { pos: 'Verb', details };
}

/**
 * Decode a parsing code string into { pos, details }.
 * @param {string} code  e.g. "N-NSF", "V-AAI-3S", "CONJ"
 * @returns {{ pos: string, details: string[] }}
 */
export function decodeParsingCode(code) {
  if (!code) return { pos: '—', details: [] };

  if (STANDALONE[code]) return { pos: STANDALONE[code], details: [] };

  const parts = code.split('-');
  const posCode = parts[0];

  if (posCode === 'V') return decodeVerb(parts);

  return decodeNominal(posCode, parts[1]);
}

/**
 * Returns a short one-line summary string for use in compact UI.
 */
export function shortParsing(code) {
  const { pos, details } = decodeParsingCode(code);
  return details.length ? `${pos} · ${details.join(' · ')}` : pos;
}
